import { useLiveQuery } from "dexie-react-hooks";
import { CalendarDays, Hash, List, SquareKanban } from "lucide-react";
import { useState } from "react";
import { colorHex, type Project, type ViewStyle } from "@todoer/shared";
import { db, useCollaborators, useMemberships, useUser } from "../lib/db.js";
import { sortProjectTree } from "../lib/hooks.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { Dialog, Select } from "./overlay.js";
import { Avatar, Button, ColorPicker, Field, Input, SegmentedControl, Switch } from "./ui.js";

export const VIEW_OPTIONS: { value: ViewStyle; label: string; icon: typeof List }[] = [
  { value: "list", label: "List", icon: List },
  { value: "board", label: "Board", icon: SquareKanban },
  { value: "calendar", label: "Calendar", icon: CalendarDays },
];

/** Create or edit a project. Returns the new project id via onSaved when creating. */
export function ProjectDialog({ project, parentId = null, onClose, onSaved }: {
  project?: Project; parentId?: string | null; onClose: () => void; onSaved?: (id: string) => void;
}) {
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);
  const [name, setName] = useState(project?.name ?? "");
  const [color, setColor] = useState(project?.color ?? "blue");
  const [parent, setParent] = useState<string | null>(project?.parentId ?? parentId);
  const [favorite, setFavorite] = useState(project?.isFavorite ?? false);
  const [view, setView] = useState<ViewStyle>(project?.viewStyle ?? "list");

  // A project cannot sit under itself or its own sub-projects.
  const blocked = new Set<string>(project ? [project.id] : []);
  if (project) {
    let grew = true;
    while (grew) { grew = false; for (const p of projects) if (p.parentId && blocked.has(p.parentId) && !blocked.has(p.id)) { blocked.add(p.id); grew = true; } }
  }
  const parentOptions = [
    { value: null as string | null, label: "No parent" },
    ...sortProjectTree(projects.filter((p) => !p.isArchived && !p.isInbox && !blocked.has(p.id))).map((p) => ({ value: p.id as string | null, label: p.name, indent: p.depth, icon: Hash, iconColor: colorHex(p.color) })),
  ];

  const save = async () => {
    const n = name.trim();
    if (!n) return;
    if (project) {
      await dispatch(cmd("project_update", { id: project.id, name: n, color, isFavorite: favorite, viewStyle: view, parentId: parent }));
      onSaved?.(project.id);
    } else {
      const id = uuid();
      const order = projects.filter((p) => p.parentId === parent).length + 1;
      await dispatch(cmd("project_add", { id, name: n, color, isFavorite: favorite, viewStyle: view, parentId: parent, childOrder: order }));
      onSaved?.(id);
    }
    onClose();
  };

  return (
    <Dialog title={project ? "Edit project" : "Add project"} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!name.trim()} onClick={() => void save()}>{project ? "Save" : "Add project"}</Button></>}>
      <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <Field label="Name" htmlFor="pd-name">
          <Input id="pd-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="e.g. Website relaunch" />
        </Field>
        <div>
          <span className="mb-1.5 block text-meta font-medium text-text-muted">Colour</span>
          <ColorPicker value={color} onChange={setColor} />
        </div>
        <div>
          <span className="mb-1.5 block text-meta font-medium text-text-muted">Parent project</span>
          <Select label="Parent project" value={parent} onChange={setParent} options={parentOptions} search={parentOptions.length > 8} />
        </div>
        <div>
          <span className="mb-1.5 block text-meta font-medium text-text-muted">Layout</span>
          <SegmentedControl label="Layout" value={view} onChange={setView} options={VIEW_OPTIONS} className="w-full" />
        </div>
        <Switch label="Add to favourites" checked={favorite} onChange={setFavorite} />
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

/** Choose which teammates can see and edit a project. */
export function ShareDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const people = useCollaborators();
  const memberships = useMemberships();
  const me = useUser();
  const members = memberships.filter((m) => m.projectId === project.id);
  const myRole = members.find((m) => m.userId === me?.id)?.role;

  return (
    <Dialog title={`Share “${project.name}”`} description="Members can see, add, edit and complete tasks, and can be assigned tasks." onClose={onClose}
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <ul className="-mx-1">
        {people.map((p) => {
          const m = members.find((x) => x.userId === p.id);
          const canChange = m?.role !== "owner" && (myRole === "owner" || p.id === me?.id || !m);
          return (
            <li key={p.id} className="flex items-center gap-3 rounded-control px-1 py-2">
              <Avatar person={p} size={32} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-body font-medium">{p.name}{p.id === me?.id ? " (you)" : ""}</div>
                <div className="truncate text-meta text-text-faint">{p.email}</div>
              </div>
              {m?.role === "owner" ? <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-meta font-medium text-text-muted">Owner</span> : (
                <Button size="sm" disabled={!canChange} variant={m ? "secondary" : "primary"}
                  onClick={() => void dispatch(cmd(m ? "project_unshare" : "project_share", { projectId: project.id, userId: p.id }))}>
                  {m ? (p.id === me?.id ? "Leave" : "Remove") : "Add"}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {people.length <= 1 && <p className="mt-3 text-body text-text-muted">Nobody else is in the workspace yet. An admin can add people in Settings → Team.</p>}
    </Dialog>
  );
}
